import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Icon from '../AppIcon';


const DashboardHeader = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [activeTimeframe, setActiveTimeframe] = useState('24h');
  const [selectedPortfolio, setSelectedPortfolio] = useState('all');
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme === 'dark' || (!savedTheme && true);
  });
  const [isConnected, setIsConnected] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login-screen');
  };

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement?.classList?.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement?.classList?.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDarkMode]);

  const timeframes = [
    { label: '24 Hours', value: '24h', icon: 'Clock' },
    { label: '7 Days', value: '7d', icon: 'Calendar' },
    { label: '30 Days', value: '30d', icon: 'Calendar' },
    { label: 'All Time', value: 'all', icon: 'Infinity' }
  ];

  const portfolios = [
    { label: 'All Portfolios', value: 'all', exchange: 'all' },
    { label: 'MexC Portfolio', value: 'mexc', exchange: 'mexc' },
    { label: 'PolyMarket Portfolio', value: 'polymarket', exchange: 'polymarket' },
    { label: 'Binance Portfolio', value: 'binance', exchange: 'binance' }
  ];

  const handleTimeframeChange = (value) => {
    setActiveTimeframe(value);
  };

  const handlePortfolioChange = (e) => {
    setSelectedPortfolio(e?.target?.value);
  };

  const handleThemeToggle = () => {
    setIsDarkMode(!isDarkMode);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-[100] bg-card border-b border-border">
      <div className="h-16 px-6 flex items-center justify-between">
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="Vault" size={24} color="var(--color-primary)" />
            </div>
            <h1 className="text-xl font-heading font-semibold text-foreground hidden sm:block">
              TheVault
            </h1>
          </div>

          <div className="hidden lg:flex items-center gap-2">
            {timeframes?.map((timeframe) => (
              <button
                key={timeframe?.value}
                onClick={() => handleTimeframeChange(timeframe?.value)}
                className={`
                  px-4 h-9 rounded-lg flex items-center gap-2 transition-smooth
                  ${activeTimeframe === timeframe?.value
                    ? 'bg-primary text-primary-foreground shadow-glow-primary'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground'
                  }
                `}
              >
                <Icon name={timeframe?.icon} size={16} />
                <span className="text-sm font-medium hidden xl:inline">{timeframe?.label}</span>
                <span className="text-sm font-medium xl:hidden">{timeframe?.value?.toUpperCase()}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="hidden md:flex items-center gap-2">
            <div className={`
              flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium
              ${isConnected 
                ? 'bg-success/10 text-success' :'bg-error/10 text-error'
              }
            `}>
              <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-success' : 'bg-error'} animate-pulse`} />
              <span>{isConnected ? 'Connected' : 'Disconnected'}</span>
            </div>
          </div>

          <select
            value={selectedPortfolio}
            onChange={handlePortfolioChange}
            className="hidden md:block h-9 px-3 pr-8 rounded-lg bg-input border border-border text-foreground text-sm font-medium appearance-none cursor-pointer transition-smooth hover:border-primary focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23F5F5F7' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 0.75rem center',
              backgroundSize: '12px'
            }}
          >
            {portfolios?.map((portfolio) => (
              <option key={portfolio?.value} value={portfolio?.value}>
                {portfolio?.label}
              </option>
            ))}
          </select>

          <button
            onClick={handleThemeToggle}
            className="hidden md:flex w-9 h-9 items-center justify-center rounded-lg bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground transition-smooth"
            aria-label="Toggle theme"
          >
            <Icon name={isDarkMode ? 'Sun' : 'Moon'} size={18} />
          </button>

          {/* User Menu */}
          <div className="relative hidden md:block">
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted text-foreground hover:bg-muted/80 transition-smooth"
            >
              <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center">
                <Icon name="User" size={14} className="text-primary" />
              </div>
              <span className="text-sm font-medium hidden xl:inline">{user?.name || 'User'}</span>
              <Icon name="ChevronDown" size={14} className="text-muted-foreground" />
            </button>
            
            {showUserMenu && (
              <div className="absolute right-0 mt-2 w-56 rounded-lg bg-card border border-border shadow-elevation-2 py-2 z-50">
                <div className="px-4 py-2 border-b border-border">
                  <p className="text-sm font-medium text-foreground">{user?.name}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                  <p className="text-xs text-primary mt-1">{user?.sharePercent}% ownership</p>
                </div>
                <button
                  onClick={handleLogout}
                  className="w-full px-4 py-2 text-left text-sm text-foreground hover:bg-muted transition-smooth flex items-center gap-2"
                >
                  <Icon name="LogOut" size={16} />
                  Sign Out
                </button>
              </div>
            )}
          </div>

          <button
            onClick={toggleMobileMenu}
            className="lg:hidden w-9 h-9 flex items-center justify-center rounded-lg bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground transition-smooth"
            aria-label="Toggle mobile menu"
          >
            <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={20} />
          </button>
        </div>
      </div>
      {isMobileMenuOpen && (
        <div className="lg:hidden border-t border-border bg-card animate-slide-down">
          <div className="p-4 space-y-4">
            <div className="space-y-2">
              <label className="text-xs font-medium text-muted-foreground caption">Timeframe</label>
              <div className="grid grid-cols-2 gap-2">
                {timeframes?.map((timeframe) => (
                  <button
                    key={timeframe?.value}
                    onClick={() => {
                      handleTimeframeChange(timeframe?.value);
                      setIsMobileMenuOpen(false);
                    }}
                    className={`
                      px-4 h-10 rounded-lg flex items-center justify-center gap-2 transition-smooth
                      ${activeTimeframe === timeframe?.value
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground'
                      }
                    `}
                  >
                    <Icon name={timeframe?.icon} size={16} />
                    <span className="text-sm font-medium">{timeframe?.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-xs font-medium text-muted-foreground caption">Portfolio</label>
              <select
                value={selectedPortfolio}
                onChange={(e) => {
                  handlePortfolioChange(e);
                  setIsMobileMenuOpen(false);
                }}
                className="w-full h-10 px-3 pr-8 rounded-lg bg-input border border-border text-foreground text-sm font-medium appearance-none cursor-pointer"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23F5F5F7' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 0.75rem center',
                  backgroundSize: '12px'
                }}
              >
                {portfolios?.map((portfolio) => (
                  <option key={portfolio?.value} value={portfolio?.value}>
                    {portfolio?.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center justify-between pt-2 border-t border-border">
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-success' : 'bg-error'} animate-pulse`} />
                <span className="text-sm font-medium text-foreground">
                  {isConnected ? 'Connected' : 'Disconnected'}
                </span>
              </div>
              <button
                onClick={handleThemeToggle}
                className="w-10 h-10 flex items-center justify-center rounded-lg bg-muted text-muted-foreground"
              >
                <Icon name={isDarkMode ? 'Sun' : 'Moon'} size={18} />
              </button>
            </div>

            {/* Mobile User Info */}
            <div className="pt-4 border-t border-border">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                  <Icon name="User" size={18} className="text-primary" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">{user?.name}</p>
                  <p className="text-xs text-muted-foreground">{user?.sharePercent}% ownership</p>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="w-full h-10 px-4 rounded-lg bg-error/10 text-error hover:bg-error/20 transition-smooth text-sm font-medium flex items-center justify-center gap-2"
              >
                <Icon name="LogOut" size={16} />
                Sign Out
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default DashboardHeader;